import { getIdToken } from '../auth/auth';
import type { Role } from './roles';

/**
 * 開発者向け API（申請・キー発行・管理者操作）のクライアント。
 * VITE_API_BASE_URL が設定されていれば SAM バックエンドを、
 * 未設定（単体デモなど）ならモックデータ + localStorage を使う。
 *
 * バックエンドのエンドポイント:
 *   POST   /developers               開発者登録の申請（要ログイン）
 *   GET    /developers/me            自分の申請状況 + 利用状況（要ログイン）
 *   PATCH  /developers/me/oauth-client  OAuth クライアントの redirect_uri 登録（要ログイン・承認済みのみ）
 *   POST   /developers/me/oauth-client/secret  client_secret 発行/ローテーション（要ログイン・承認済みのみ）
 *   GET    /oauth/client-info        同意画面用の client 情報取得（認証不要）
 *   POST   /oauth/consent            同意画面での承認/拒否 → 認可コード発行（要ログイン）
 *   GET    /admin/developers         開発者一覧（要管理者）
 *   PATCH  /admin/developers/{sub}   ステータス/クォータ変更（要管理者）
 *   GET    /admin/usage?days=14      利用状況集計（要管理者）
 *   GET    /admin/animations         アニメーション一覧（要管理者）
 *   DELETE /admin/animations/{id}    アニメーション削除（S3実体も削除、要管理者）
 *   PATCH  /admin/animations/{id}    公開/非公開切替（要管理者）
 *   GET    /admin/users              Cognitoユーザー一覧（email前方一致検索可、要管理者）
 *   POST   /admin/users/{sub}/ban    ユーザー無効化（任意でBAN理由メモを保存、要管理者）
 *   POST   /admin/users/{sub}/unban  ユーザー無効化解除（要管理者）
 *   PUT    /admin/users/{sub}/roles  ロール変更（Cognito Group を追加/削除、要管理者）
 *   GET    /admin/stats              概要KPI（要管理者）
 *   GET    /admin/attention          要対応件数（未対応の問い合わせ/通報、審査中の開発者申請。要管理者）
 *   GET    /admin/tags              タグ一覧（要管理者）
 *   POST   /admin/tags              タグ新規登録（要管理者）
 *   PATCH  /admin/tags/{name}       タグ説明編集・リネーム（統合）（要管理者）
 *   DELETE /admin/tags/{name}       タグ削除（要管理者）
 *   GET    /admin/vpm/versions                             VPM(Unityパッケージ)バージョン一覧（全パッケージ、要管理者）
 *   PATCH  /admin/vpm/versions/{packageName}/{version}      配信ステータス変更(published/suspended)（要管理者）
 *   DELETE /admin/vpm/versions/{packageName}/{version}      バージョン完全削除(zip実体も削除)（要管理者）
 *   GET    /admin/contacts           問い合わせ履歴一覧（要管理者）
 *   PATCH  /admin/contacts/{id}      問い合わせステータス変更（要管理者）
 *   GET    /admin/reports            通報一覧（要管理者）
 *   PATCH  /admin/reports/{id}       通報ステータス変更（要管理者）
 *   GET    /admin/audit-logs         管理操作の監査ログ一覧（要管理者）
 *   POST   /admin/pose-server/start  pose-server（PC常駐GPU推論サーバー）の起動（要管理者）
 *   POST   /admin/pose-server/stop   pose-server の停止（要管理者）
 *   POST   /admin/pose-server/accepting  利用者からの画像受付ON/OFFの切り替え（要管理者）
 *   GET    /connected-apps           ホーム掲載中(approved)の連携アプリ一覧（認証不要）
 *   GET    /developers/me/app-listings  自分の掲載申請一覧（要ログイン、古い順、全件）
 *   POST   /developers/me/app-listings  掲載申請の新規登録（要ログイン・承認済み開発者のみ、status は pending。11件目は409）
 *   PUT    /developers/me/app-listings/{appId}  掲載情報の更新（要ログイン、保存すると常に pending に戻る。停止中は403）
 *   DELETE /developers/me/app-listings/{appId}  掲載申請の取り下げ（要ログイン）
 *   POST   /developers/me/app-listings/icon-upload-url  アプリアイコン用 presigned PUT URL 発行（要ログイン）
 *   GET    /admin/app-listings       掲載申請一覧（新しい順、要管理者）
 *   PATCH  /admin/app-listings/{appId}  ステータス変更（approved/rejected/suspended、要管理者）
 *   GET    /admin/similarity-flags        モーション類似検出結果一覧（新着順、要管理者）
 *   PATCH  /admin/similarity-flags/{id}   類似検出結果の対応ステータス変更（要管理者）
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL;

/** ログイン中なら Authorization ヘッダを付ける。 */
async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = { ...(await authHeaders()), ...(init?.headers as Record<string, string>) };
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export type DeveloperStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

export interface DeveloperInfo {
  sub: string;
  name: string;
  appName: string;
  appDescription: string;
  purpose: string;
  status: DeveloperStatus;
  createdAt: string;
  updatedAt: string;
  dailyQuota: number;
  clientId?: string;
  redirectUris?: string[];
  allowedScopes?: string[];
  // 月間の無料リクエスト数。既定 50,000。管理者がお問い合わせ対応で個別に引き上げる場合がある。
  monthlyQuota: number;
  // 管理者アカウント特典: true なら月次クォータなしで無制限に利用できる。
  unlimited?: boolean;
  // client_secret を発行済みか。OAuth 連携（/oauth/token・/oauth/revoke）の
  // 利用には client_secret の発行が必須のため、false の間は連携できない。
  hasClientSecret?: boolean;
  // 発行済み client_secret の先頭表示用（平文は発行直後のレスポンスでしか取得できない）。
  clientSecretPrefix?: string;
  // 利用規約等に同意した日時（ISO8601）。同意必須化前の既存レコードには無い。
  termsAcceptedAt?: string;
}

export interface DailyUsage {
  /** 'YYYY-MM-DD' */
  date: string;
  requests: number;
  errors: number;
  rateLimited: number;
  lastAt?: string;
}

export interface UsageRow extends DailyUsage {
  sub: string;
}

/** 管理者コンソール用のアニメーション情報（visibility は必ず埋まっている）。 */
export interface AdminAnimation {
  id: string;
  title: string;
  description: string;
  author: string;
  ownerId?: string;
  ownerNickname?: string;
  ownerIconUrl?: string;
  ownerEmail?: string;
  visibility: 'public' | 'private';
  createdAt: string;
  likes: number;
  playCount?: number;
  downloadCount?: number;
  thumbnailUrl?: string;
  vrmaUrl: string;
  tags: string[];
}

/** 管理者コンソール用の Cognito ユーザー情報。 */
export interface AdminUser {
  sub: string;
  email?: string;
  username: string;
  enabled: boolean;
  status: string;
  createdAt: string;
  nickname?: string;
  iconUrl?: string;
  roles: Role[];
  // 無効化ユーザーのBAN理由メモ（任意）。enabled=false のユーザーにのみ設定されうる。
  banReason?: string;
}

/** 管理者コンソール「概要」タブ用の KPI。 */
export interface AdminStats {
  animations: { total: number; public: number; private: number; totalPlays: number; totalDownloads: number };
  developers: { total: number; pending: number; approved: number; rejected: number; suspended: number };
  users: { total: number; enabled: number; disabled: number; truncated: boolean };
}

/** 管理画面のバッジ表示用「要対応」件数。上限200で打ち切った概数（正確なカウントではない）。 */
export interface AdminAttention {
  openContacts: number;
  openReports: number;
  pendingDevelopers: number;
  openSimilarityFlags: number;
}

export interface ApplyDeveloperFields {
  termsAccepted: boolean;
}

/** 管理者コンソール用のタグマスタ情報。 */
export interface AdminTag {
  name: string;
  description?: string;
  // 未登録タグ（アニメーションには使われているがタグマスタに無いもの）には無い。
  createdAt?: string;
  // 実際にアニメーションへ付与されている件数。
  usageCount?: number;
  // true ならタグマスタに存在しない「未登録タグ」（削除不可、リネームのみ可能）。
  unregistered?: boolean;
}

/** 管理者コンソール用のカテゴリマスタ情報。各アニメーションはカテゴリを最大1つ持つ。 */
export interface AdminCategory {
  name: string;
  description?: string;
  createdAt: string;
}

export type VpmVersionStatus = 'published' | 'suspended';

/** 管理者コンソール用の VPM (Unityパッケージ) バージョン情報。 */
export interface AdminVpmVersion {
  packageName: string;
  version: string;
  status: VpmVersionStatus;
  zipFileName: string;
  zipSHA256: string;
  manifest: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export type ContactCategory = 'general' | 'rights' | 'privacy' | 'bug' | 'other';

export type ContactStatus = 'open' | 'closed';

/** 管理者コンソール用の問い合わせ履歴情報。 */
export interface AdminContact {
  id: string;
  createdAt: string;
  category: ContactCategory;
  email: string;
  name?: string;
  message: string;
  sourceIp: string;
  userAgent?: string;
  status: ContactStatus;
}

export type ReportCategory = 'rights' | 'inappropriate' | 'spam' | 'other';

export type ReportStatus = 'open' | 'closed';

/** 再通報で上書きされる前の過去エントリ（古い順）。 */
export interface ReportHistoryEntry {
  createdAt: string;
  category: ReportCategory;
  detail?: string;
  status: ReportStatus;
}

/** 管理者コンソール用の通報情報。 */
export interface AdminReport {
  id: string;
  createdAt: string;
  animationId: string;
  // 通報時点のタイトルをスナップショット（対象削除後も一覧で分かるように）。
  animationTitle: string;
  reporterSub: string;
  category: ReportCategory;
  detail?: string;
  status: ReportStatus;
  // 同一ユーザーによる再通報があった場合の過去エントリ（無ければ初回通報のまま）。
  history?: ReportHistoryEntry[];
}

/** 管理者コンソール用のモーション類似検出結果（再投稿ブロックのレビューキュー）情報。 */
export interface AdminSimilarityFlag {
  id: string;
  createdAt: string;
  animationId: string;
  // 検出時点のタイトルのスナップショット（対象削除後も一覧で分かるように）。
  animationTitle: string;
  // 一致したブロックリストエントリの id。
  blocklistAnimationId: string;
  // 一致したブロックリストエントリのタイトルのスナップショット（削除済みのため元アニメーションは辿れない）。
  blocklistTitle: string;
  // DTW精密照合の類似度（0〜1）。
  similarity: number;
  // true ならミラー（左右反転）版との照合で一致した。
  mirrored: boolean;
  // shadow モードでの検出時、もし enforce モードだったら BLOCK 閾値以上でブロックされていたか。
  shadowWouldHaveBlocked: boolean;
  status: 'open' | 'closed';
}

/** 管理者コンソール用の監査ログ情報。 */
export interface AdminAuditLog {
  id: string;
  createdAt: string;
  actorSub: string;
  actorEmail?: string;
  action: string;
  targetType: string;
  targetId: string;
  detail?: string;
}

export type AppListingStatus = 'pending' | 'approved' | 'rejected' | 'suspended';

/** 開発者ポータルの「連携アプリケーション」掲載申請。開発者ごとに複数件持てる（最大 MAX_APP_LISTINGS 件）。 */
export interface AppListingInfo {
  // id === appId（互換のため両方のフィールドを持つ）。
  id: string;
  appId: string;
  sub: string;
  name: string;
  url: string;
  iconKey?: string;
  iconUrl?: string;
  // 管理者コンソールの一覧に表示する、申請元の開発者アプリ名（DeveloperInfo.appName）。
  developerAppName?: string;
  status: AppListingStatus;
  createdAt: string;
  updatedAt: string;
}

/** 開発者1人が掲載申請できるアプリの最大件数。 */
export const MAX_APP_LISTINGS = 10;

/** 掲載申請の表示名の最大文字数。 */
export const APP_LISTING_NAME_MAX = 30;

/** ホームの「連携アプリケーション」セクション用（GET /connected-apps、認証不要）。 */
export interface ConnectedAppInfo {
  id: string;
  name: string;
  url: string;
  iconUrl?: string;
}

// ---- モックモード用のローカルストア ---------------------------------------
//
// モック環境では「開発者」はブラウザに 1 件しか存在しない想定
// （= 自分の申請のみ）。/admin 系のモックもこの 1 件だけを対象にするため、
// 複数開発者の管理を再現するものではない点に注意。

const DEV_KEY = 'vrma-hub:dev-application';
const DEFAULT_DAILY_QUOTA = 1000;
// backend/src/lib/types.ts の FREE_MONTHLY_QUOTA と揃える。
const DEFAULT_MONTHLY_QUOTA = 50000;
/** モックモードの固定ユーザー（AuthContext.tsx の LOCAL_USER と同じ想定）。 */
const MOCK_SUB = 'local';
const MOCK_NAME = 'you';

// OAuth クライアント関連のモック用デフォルト値（backend/src/lib/oauthClient.ts と揃える）。
const DEFAULT_SCOPES = ['read:animations', 'read:favorites', 'write:animations', 'write:favorites'];
export const MAX_REDIRECT_URIS = 5;

/** モック用の clientId を生成する（16文字hex小文字）。 */
function generateClientId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function loadDeveloper(): DeveloperInfo | null {
  try {
    const raw = localStorage.getItem(DEV_KEY);
    if (!raw) return null;
    const dev = JSON.parse(raw) as DeveloperInfo;
    // clientId が無い（マイグレーション前の）レコードなら、読み取り時に発行して保存する。
    if (!dev.clientId) {
      const migrated: DeveloperInfo = {
        ...dev,
        clientId: generateClientId(),
        redirectUris: dev.redirectUris ?? [],
        allowedScopes: dev.allowedScopes ?? DEFAULT_SCOPES,
      };
      saveDeveloper(migrated);
      return migrated;
    }
    // monthlyQuota が無い（旧レコード）なら、無料枠として遅延マイグレーションする。
    if (dev.monthlyQuota === undefined) {
      const migrated: DeveloperInfo = { ...dev, monthlyQuota: DEFAULT_MONTHLY_QUOTA };
      saveDeveloper(migrated);
      return migrated;
    }
    return dev;
  } catch {
    return null;
  }
}

function saveDeveloper(dev: DeveloperInfo): void {
  localStorage.setItem(DEV_KEY, JSON.stringify(dev));
}

const TAGS_KEY = 'vrma-hub:admin-tags';

function loadTags(): AdminTag[] {
  try {
    const raw = localStorage.getItem(TAGS_KEY);
    return raw ? (JSON.parse(raw) as AdminTag[]) : [];
  } catch {
    return [];
  }
}

function saveTags(tags: AdminTag[]): void {
  localStorage.setItem(TAGS_KEY, JSON.stringify(tags));
}

// client.ts の CATEGORIES_STORAGE_KEY と同じキー（管理コンソールで登録したカテゴリを共有する）。
const CATEGORIES_KEY = 'vrma-hub:admin-categories';

function loadCategories(): AdminCategory[] {
  try {
    const raw = localStorage.getItem(CATEGORIES_KEY);
    return raw ? (JSON.parse(raw) as AdminCategory[]) : [];
  } catch {
    return [];
  }
}

function saveCategories(categories: AdminCategory[]): void {
  localStorage.setItem(CATEGORIES_KEY, JSON.stringify(categories));
}

// モックでは「掲載申請」はブラウザに複数件（= 自分の申請一覧）持てる。
// /admin/app-listings のモックもこの一覧だけを対象にする（複数開発者の管理は再現できない）。
const APP_LISTINGS_KEY = 'vrma-hub:app-listings';

function loadAppListings(): AppListingInfo[] {
  try {
    const raw = localStorage.getItem(APP_LISTINGS_KEY);
    return raw ? (JSON.parse(raw) as AppListingInfo[]) : [];
  } catch {
    return [];
  }
}

function saveAppListings(listings: AppListingInfo[]): void {
  localStorage.setItem(APP_LISTINGS_KEY, JSON.stringify(listings));
}

/** dataURL（data:image/...;base64,...）に変換する。モックモードでのアイコン永続化用。 */
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('画像の読み込みに失敗しました'));
    reader.readAsDataURL(blob);
  });
}

/** 直近 days 日分の疑似利用データを生成する（確定的ではない）。 */
function mockUsage(days: number): DailyUsage[] {
  const result: DailyUsage[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const date = d.toISOString().slice(0, 10);
    const requests = Math.floor(Math.random() * 200);
    const errors = Math.floor(Math.random() * Math.min(5, requests));
    const rateLimited = Math.random() < 0.15 ? Math.floor(Math.random() * 10) : 0;
    result.push({
      date,
      requests,
      errors,
      rateLimited,
      lastAt: requests > 0 ? new Date(d.getTime() + 12 * 3600 * 1000).toISOString() : undefined,
    });
  }
  return result;
}

function randomKeySuffix(len: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let out = '';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

// ---- 連携アプリ（公開） ---------------------------------------------------------

/** ホーム掲載中(approved)の連携アプリ一覧（認証不要）。 */
export async function fetchConnectedApps(): Promise<ConnectedAppInfo[]> {
  if (API_BASE) {
    const res = await fetch(`${API_BASE}/connected-apps`);
    if (!res.ok) throw new Error(`連携アプリ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as ConnectedAppInfo[];
  }
  return loadAppListings()
    .filter((l) => l.status === 'approved')
    .map((l) => ({ id: l.appId, name: l.name, url: l.url, iconUrl: l.iconUrl }));
}

// ---- 開発者本人向け ---------------------------------------------------------

/** 自分の申請状況 + 利用状況。未申請なら undefined（404 相当）。 */
export async function fetchMyDeveloper(): Promise<(DeveloperInfo & { usage: DailyUsage[] }) | undefined> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me');
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error(`申請状況の取得に失敗しました (${res.status})`);
    return (await res.json()) as DeveloperInfo & { usage: DailyUsage[] };
  }
  const dev = loadDeveloper();
  if (!dev) return undefined;
  return { ...dev, usage: mockUsage(7) };
}

/** 開発者登録を申請する。申請済み（pending/approved/suspended）なら 409 相当でエラー。 */
export async function applyDeveloper(fields: ApplyDeveloperFields): Promise<DeveloperInfo> {
  if (API_BASE) {
    const res = await apiFetch('/developers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(fields),
    });
    if (res.status === 409) throw new Error('既に開発者登録を申請済みです');
    if (res.status === 401) throw new Error('ログインが必要です');
    if (!res.ok) throw new Error(`申請に失敗しました (${res.status})`);
    return (await res.json()) as DeveloperInfo;
  }
  if (!fields.termsAccepted) {
    throw new Error('利用規約への同意が必要です');
  }
  const existing = loadDeveloper();
  if (existing && existing.status !== 'rejected') {
    throw new Error('既に開発者登録を申請済みです');
  }
  const now = new Date().toISOString();
  const dev: DeveloperInfo = {
    sub: MOCK_SUB,
    name: MOCK_NAME,
    // アプリ個別の情報は「連携アプリケーション掲載申請」側で扱うため、開発者登録では空で保存する。
    appName: '',
    appDescription: '',
    purpose: '',
    status: 'pending',
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    dailyQuota: existing?.dailyQuota ?? DEFAULT_DAILY_QUOTA,
    termsAcceptedAt: now,
    // clientId は申請のたびに新しく発行する（rejected からの再申請では過去の値も破棄）。
    clientId: generateClientId(),
    redirectUris: [],
    allowedScopes: [...DEFAULT_SCOPES],
    // 引き上げ済みの月間クォータがあれば引き継ぐ（rejected → 再申請で無料枠に戻ることはない）。
    monthlyQuota: existing?.monthlyQuota ?? DEFAULT_MONTHLY_QUOTA,
  };
  saveDeveloper(dev);
  return dev;
}

/** OAuth クライアントの redirect_uri を登録する。承認済みでなければ 403 相当でエラー。 */
export async function updateOAuthClient(redirectUris: string[]): Promise<DeveloperInfo> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me/oauth-client', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ redirectUris }),
    });
    if (res.status === 403) throw new Error('承認済みの開発者のみ設定できます');
    if (!res.ok) throw new Error(`redirect_uri の保存に失敗しました (${res.status})`);
    return (await res.json()) as DeveloperInfo;
  }
  const dev = loadDeveloper();
  if (!dev || dev.status !== 'approved') {
    throw new Error('承認済みの開発者のみ設定できます');
  }
  const updated: DeveloperInfo = {
    ...dev,
    redirectUris: [...new Set(redirectUris)],
    updatedAt: new Date().toISOString(),
  };
  saveDeveloper(updated);
  return updated;
}

/**
 * client_secret を発行（未発行時）/ ローテーション（発行済み時、旧 secret は即失効）する。
 * OAuth 連携（/oauth/token・/oauth/revoke）には client_secret が必須のため、
 * 発行済みでない開発者はこれを呼ぶまで OAuth 連携を利用できない。
 * 承認済みでなければ 403 相当でエラー。
 */
export async function issueClientSecret(): Promise<{ clientSecret: string; clientSecretPrefix: string }> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me/oauth-client/secret', { method: 'POST' });
    if (res.status === 403) throw new Error('承認済みの開発者のみ client_secret を発行できます');
    if (!res.ok) throw new Error(`client_secret の発行に失敗しました (${res.status})`);
    return (await res.json()) as { clientSecret: string; clientSecretPrefix: string };
  }
  const dev = loadDeveloper();
  if (!dev || dev.status !== 'approved') {
    throw new Error('承認済みの開発者のみ client_secret を発行できます');
  }
  const suffix = randomKeySuffix(32);
  const clientSecret = `vhcs_mock_${suffix}`;
  const clientSecretPrefix = `${clientSecret.slice(0, 12)}…`;
  saveDeveloper({ ...dev, hasClientSecret: true, clientSecretPrefix, updatedAt: new Date().toISOString() });
  return { clientSecret, clientSecretPrefix };
}

// ---- 開発者本人向け: 連携アプリケーション掲載申請（複数アプリ対応） ------------------------

/** 自分の掲載申請一覧（要ログイン、古い順）。 */
export async function fetchMyAppListings(): Promise<AppListingInfo[]> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me/app-listings');
    if (!res.ok) throw new Error(`掲載申請一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AppListingInfo[];
  }
  return [...loadAppListings()].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}

/**
 * 掲載申請を新規登録する（要ログイン）。status は常に pending。
 * 承認済みの開発者でなければ 403、既に MAX_APP_LISTINGS 件申請済みなら 409 相当でエラー
 * （メッセージはレスポンス body の message があればそれを優先する）。
 */
export async function createAppListing(input: {
  name: string;
  url: string;
  iconKey?: string;
}): Promise<AppListingInfo> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me/app-listings', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 403) {
      // backend/src/lib/http.ts の forbidden() は { error: message } の形で返す。
      const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
      throw new Error(body?.message ?? body?.error ?? '承認済みの開発者のみ掲載申請できます');
    }
    if (res.status === 409) {
      const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
      throw new Error(body?.message ?? body?.error ?? `掲載申請できるアプリは最大${MAX_APP_LISTINGS}件までです`);
    }
    if (res.status === 400) {
      const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
      throw new Error(body?.message ?? body?.error ?? '入力内容を確認してください');
    }
    if (!res.ok) throw new Error(`掲載申請の登録に失敗しました (${res.status})`);
    return (await res.json()) as AppListingInfo;
  }
  const dev = loadDeveloper();
  if (!dev || dev.status !== 'approved') {
    throw new Error('承認済みの開発者のみ掲載申請できます');
  }
  const listings = loadAppListings();
  if (listings.length >= MAX_APP_LISTINGS) {
    throw new Error(`掲載申請できるアプリは最大${MAX_APP_LISTINGS}件までです`);
  }
  const now = new Date().toISOString();
  const appId = crypto.randomUUID();
  const listing: AppListingInfo = {
    id: appId,
    appId,
    sub: dev.sub,
    name: input.name,
    url: input.url,
    iconKey: input.iconKey,
    // モックでは presigned アップロードを行わないため、iconKey（dataURL）をそのまま iconUrl として扱う。
    iconUrl: input.iconKey,
    developerAppName: dev.appName,
    status: 'pending',
    createdAt: now,
    updatedAt: now,
  };
  saveAppListings([...listings, listing]);
  return listing;
}

/**
 * 掲載情報を更新する（要ログイン）。保存すると status は常に pending（再審査）に戻る。
 * 掲載が停止中の場合は 403、他人の申請/不存在の場合は 404 相当でエラー
 * （メッセージはレスポンス body の message があればそれを優先する）。
 */
export async function updateMyAppListing(
  appId: string,
  input: { name: string; url: string; iconKey?: string },
): Promise<AppListingInfo> {
  if (API_BASE) {
    const res = await apiFetch(`/developers/me/app-listings/${encodeURIComponent(appId)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 403) {
      const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
      throw new Error(body?.message ?? body?.error ?? '管理者により掲載停止中のため編集できません');
    }
    if (res.status === 404) throw new Error('対象の掲載申請が見つかりません');
    if (res.status === 400) {
      const body = (await res.json().catch(() => null)) as { message?: string; error?: string } | null;
      throw new Error(body?.message ?? body?.error ?? '入力内容を確認してください');
    }
    if (!res.ok) throw new Error(`掲載申請の保存に失敗しました (${res.status})`);
    return (await res.json()) as AppListingInfo;
  }
  const listings = loadAppListings();
  const idx = listings.findIndex((l) => l.appId === appId);
  if (idx === -1) throw new Error('対象の掲載申請が見つかりません');
  const existing = listings[idx];
  if (existing.status === 'suspended') {
    throw new Error('管理者により掲載停止中のため編集できません');
  }
  const updated: AppListingInfo = {
    ...existing,
    name: input.name,
    url: input.url,
    iconKey: input.iconKey ?? existing.iconKey,
    iconUrl: input.iconKey ?? existing.iconUrl,
    status: 'pending',
    updatedAt: new Date().toISOString(),
  };
  const next = [...listings];
  next[idx] = updated;
  saveAppListings(next);
  return updated;
}

/** 掲載申請を取り下げる（要ログイン）。取り下げ後は同じアプリを再申請できる。 */
export async function deleteMyAppListing(appId: string): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(`/developers/me/app-listings/${encodeURIComponent(appId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`取り下げに失敗しました (${res.status})`);
    return;
  }
  saveAppListings(loadAppListings().filter((l) => l.appId !== appId));
}

/** アプリアイコン用 presigned PUT URL を発行する（要ログイン）。 */
export async function createAppIconUploadUrl(
  contentType: 'image/png' | 'image/jpeg' | 'image/webp',
): Promise<{ uploadUrl: string; key: string; publicUrl: string }> {
  if (API_BASE) {
    const res = await apiFetch('/developers/me/app-listings/icon-upload-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType }),
    });
    if (!res.ok) throw new Error(`アップロードURLの取得に失敗しました (${res.status})`);
    return (await res.json()) as { uploadUrl: string; key: string; publicUrl: string };
  }
  throw new Error('モックモードでは使用できません');
}

const ALLOWED_APP_ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const APP_ICON_MAX_BYTES = 1024 * 1024;

/**
 * File を受け取ってアプリアイコンをアップロードし、iconKey を返す（要ログイン）。
 * API モードでは presigned PUT で S3 へ直接アップロードし、返る key をそのまま渡す。
 * モックモードでは dataURL 化してそのまま key（= iconUrl）として使う。
 */
export async function uploadAppIcon(file: File): Promise<string> {
  if (!ALLOWED_APP_ICON_TYPES.includes(file.type)) {
    throw new Error('画像形式は png / jpeg / webp のみ対応しています');
  }
  if (file.size > APP_ICON_MAX_BYTES) {
    throw new Error('画像サイズは1MB以内にしてください');
  }
  if (API_BASE) {
    const { uploadUrl, key } = await createAppIconUploadUrl(file.type as 'image/png' | 'image/jpeg' | 'image/webp');
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });
    if (!putRes.ok) throw new Error(`アイコンのアップロードに失敗しました (${putRes.status})`);
    return key;
  }
  return blobToDataUrl(file);
}

// ---- OAuth 認可コードフロー（/oauth/authorize 同意画面）------------------------

export interface OAuthClientInfoResponse {
  clientId: string;
  appName: string;
  appDescription: string;
  ownerName: string;
  redirectUri: string;
  scopes: string[];
}

export interface OAuthConsentRequest {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  decision: 'approve' | 'deny';
}

/** scope 文字列（空白区切り）をパースする。空・未指定なら空配列。backend/src/lib/oauthCode.ts と揃える。 */
function parseScopeMock(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(/\s+/).filter(Boolean);
}

function isScopeAllowedMock(requested: string[], allowed: string[]): boolean {
  return requested.every((s) => allowed.includes(s));
}

/** redirect_uri にクエリパラメータを連結する（既に ? があれば & を使う素直な実装）。 */
function appendQueryMock(redirectUri: string, params: Record<string, string>): string {
  const sep = redirectUri.includes('?') ? '&' : '?';
  const query = Object.entries(params)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return `${redirectUri}${sep}${query}`;
}

/** モック用の認可コードを生成する（Phase 3 未実装のためトークン交換には使われない）。 */
function generateMockAuthCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function mockOAuthClientInfo(params: {
  clientId: string;
  redirectUri: string;
  scope: string;
}): OAuthClientInfoResponse | { error: string } {
  const dev = loadDeveloper();
  if (!dev || dev.status !== 'approved' || !dev.clientId || dev.clientId !== params.clientId) {
    return { error: 'invalid_client' };
  }
  if (!dev.redirectUris?.includes(params.redirectUri)) {
    return { error: 'invalid_redirect_uri' };
  }
  const scopes = parseScopeMock(params.scope);
  if (!isScopeAllowedMock(scopes, dev.allowedScopes ?? [])) {
    return { error: 'invalid_scope' };
  }
  return {
    clientId: dev.clientId,
    appName: dev.appName,
    appDescription: dev.appDescription,
    ownerName: dev.name,
    redirectUri: params.redirectUri,
    scopes,
  };
}

function mockOAuthConsent(body: OAuthConsentRequest): { redirectTo: string } | { error: string } {
  const info = mockOAuthClientInfo({
    clientId: body.clientId,
    redirectUri: body.redirectUri,
    scope: body.scope,
  });
  if ('error' in info) return info;

  // eslint-disable-next-line no-control-regex
  if (!body.state || body.state.length > 512 || !/^[\x20-\x7e]+$/.test(body.state) || body.state.includes('#')) {
    return { error: 'invalid_state' };
  }
  if (body.codeChallengeMethod !== 'S256') {
    return { error: 'unauthorized_client' };
  }
  if (!/^[A-Za-z0-9\-._~]{43,128}$/.test(body.codeChallenge)) {
    return { error: 'invalid_challenge' };
  }

  if (body.decision === 'deny') {
    return { redirectTo: appendQueryMock(body.redirectUri, { error: 'access_denied', state: body.state }) };
  }

  const code = generateMockAuthCode();
  return { redirectTo: appendQueryMock(body.redirectUri, { code, state: body.state }) };
}

/** /oauth/authorize 同意画面用に client 情報を取得する。認証不要。 */
export async function fetchOAuthClientInfo(params: {
  clientId: string;
  redirectUri: string;
  scope: string;
}): Promise<OAuthClientInfoResponse | { error: string }> {
  if (API_BASE) {
    const qs = new URLSearchParams({
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      scope: params.scope,
    });
    const res = await fetch(`${API_BASE}/oauth/client-info?${qs.toString()}`);
    return (await res.json().catch(() => ({ error: `unexpected_response_${res.status}` }))) as
      | OAuthClientInfoResponse
      | { error: string };
  }
  return mockOAuthClientInfo(params);
}

/** 同意画面での承認/拒否を送信する。要ログイン。 */
export async function postOAuthConsent(
  body: OAuthConsentRequest,
): Promise<{ redirectTo: string } | { error: string }> {
  if (API_BASE) {
    const res = await apiFetch('/oauth/consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 401) return { error: 'unauthorized' };
    return (await res.json().catch(() => ({ error: `unexpected_response_${res.status}` }))) as
      | { redirectTo: string }
      | { error: string };
  }
  return mockOAuthConsent(body);
}

// ---- 管理者向け ---------------------------------------------------------------

/** 開発者一覧（要管理者）。 */
export async function fetchAdminDevelopers(): Promise<DeveloperInfo[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/developers');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`開発者一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as DeveloperInfo[];
  }
  // モックでは自分の申請 1 件のみが対象（複数開発者の管理は再現できない）。
  const dev = loadDeveloper();
  return dev ? [dev] : [];
}

/** ステータス/クォータの変更（要管理者）。 */
export async function updateDeveloper(
  sub: string,
  patch: { status?: DeveloperStatus; dailyQuota?: number; monthlyQuota?: number },
): Promise<DeveloperInfo> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/developers/${encodeURIComponent(sub)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as DeveloperInfo;
  }
  const dev = loadDeveloper();
  if (!dev || dev.sub !== sub) throw new Error('対象の開発者が見つかりません');
  const updated: DeveloperInfo = {
    ...dev,
    ...(patch.status ? { status: patch.status } : {}),
    ...(patch.dailyQuota !== undefined ? { dailyQuota: patch.dailyQuota } : {}),
    ...(patch.monthlyQuota !== undefined ? { monthlyQuota: patch.monthlyQuota } : {}),
    updatedAt: new Date().toISOString(),
  };
  saveDeveloper(updated);
  return updated;
}

/** 利用状況集計（要管理者）。 */
export async function fetchAdminUsage(
  days: number,
): Promise<{ developers: DeveloperInfo[]; usage: UsageRow[] }> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/usage?days=${days}`);
    if (!res.ok) throw new Error(`利用状況の取得に失敗しました (${res.status})`);
    return (await res.json()) as { developers: DeveloperInfo[]; usage: UsageRow[] };
  }
  // モックでは自分の申請 1 件分の疑似データのみを返す。
  const dev = loadDeveloper();
  if (!dev) return { developers: [], usage: [] };
  const usage = mockUsage(days).map((u) => ({ ...u, sub: dev.sub }));
  return { developers: [dev], usage };
}

/** アニメーション一覧（公開/非公開問わず、要管理者）。公式サンプルも通常のレコードとして含まれる。 */
export async function fetchAdminAnimations(): Promise<AdminAnimation[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/animations');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`アニメーション一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminAnimation[];
  }
  // モックではアニメーションの一覧管理を再現しないため常に空。
  return [];
}

/** アニメーションを削除する（S3 実体も削除、要管理者）。
 *  opts.blockFingerprint を true にすると、削除前にフィンガープリントをブロックリストへ登録し、
 *  以降の同一・類似モーションの再投稿を自動検出・ブロックする。 */
export async function deleteAdminAnimation(id: string, opts?: { blockFingerprint?: boolean }): Promise<void> {
  if (API_BASE) {
    const init: RequestInit = { method: 'DELETE' };
    if (opts?.blockFingerprint) {
      init.headers = { 'Content-Type': 'application/json' };
      init.body = JSON.stringify({ blockFingerprint: true });
    }
    const res = await apiFetch(`/admin/animations/${encodeURIComponent(id)}`, init);
    if (!res.ok) throw new Error(`削除に失敗しました (${res.status})`);
    return;
  }
  return Promise.resolve();
}

/** アニメーションの公開/非公開を切り替える（要管理者）。 */
export async function updateAdminAnimation(
  id: string,
  patch: { visibility: 'public' | 'private' },
): Promise<AdminAnimation> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/animations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminAnimation;
  }
  throw new Error('モックモードでは公開設定の変更はサポートされていません。');
}

/** Cognito ユーザー一覧（要管理者）。paginationToken を渡すと続きのページを取得する。
 *  email を渡すと前方一致で絞り込む（サーバー検索。paginationToken と併用可）。 */
export async function fetchAdminUsers(
  paginationToken?: string,
  email?: string,
): Promise<{ users: AdminUser[]; paginationToken?: string }> {
  if (API_BASE) {
    const params = new URLSearchParams();
    if (paginationToken) params.set('paginationToken', paginationToken);
    if (email) params.set('email', email);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await apiFetch(`/admin/users${qs}`);
    if (!res.ok) throw new Error(`ユーザー一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as { users: AdminUser[]; paginationToken?: string };
  }
  // モックではユーザー管理を再現しないため常に空。
  return { users: [], paginationToken: undefined };
}

/** ユーザーを無効化し、既存セッションを全て失効させる（要管理者）。自分自身は指定不可。
 *  reason（任意・200文字以内）は BAN 理由メモとして保存される。 */
export async function banUser(sub: string, reason?: string): Promise<AdminUser> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/users/${encodeURIComponent(sub)}/ban`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason?.trim() || undefined }),
    });
    if (res.status === 400) {
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error ?? '自分自身は無効化できません');
    }
    if (!res.ok) throw new Error(`無効化に失敗しました (${res.status})`);
    return (await res.json()) as AdminUser;
  }
  return Promise.resolve({
    sub,
    email: undefined,
    username: sub,
    enabled: false,
    status: '',
    createdAt: new Date().toISOString(),
    roles: [],
    banReason: reason?.trim() || undefined,
  });
}

/** ユーザーの無効化を解除する（要管理者）。 */
export async function unbanUser(sub: string): Promise<AdminUser> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/users/${encodeURIComponent(sub)}/unban`, { method: 'POST' });
    if (!res.ok) throw new Error(`無効化解除に失敗しました (${res.status})`);
    return (await res.json()) as AdminUser;
  }
  return Promise.resolve({
    sub,
    email: undefined,
    username: sub,
    enabled: true,
    status: '',
    createdAt: new Date().toISOString(),
    roles: [],
  });
}

/** ロールを変更する（要管理者）。自分自身は指定不可。モックモードでは未サポート。 */
export async function updateUserRoles(
  sub: string,
  roles: Role[],
): Promise<{ sub: string; roles: Role[] }> {
  if (!API_BASE) {
    throw new Error('モックモードではロール変更はサポートされていません。');
  }
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE}/admin/users/${encodeURIComponent(sub)}/roles`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify({ roles }),
  });
  if (res.status === 400) throw new Error('自分自身のロールは変更できません');
  if (!res.ok) throw new Error(`ロール変更に失敗しました (${res.status})`);
  return (await res.json()) as { sub: string; roles: Role[] };
}

/** 管理者コンソール「概要」タブ用の KPI（要管理者）。 */
export async function fetchAdminStats(): Promise<AdminStats> {
  if (API_BASE) {
    const res = await apiFetch('/admin/stats');
    if (!res.ok) throw new Error(`統計情報の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminStats;
  }
  return {
    animations: { total: 0, public: 0, private: 0, totalPlays: 0, totalDownloads: 0 },
    developers: { total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 },
    users: { total: 0, enabled: 0, disabled: 0, truncated: false },
  };
}

/** 管理画面のバッジ表示用「要対応」件数（要管理者）。モックモードでは常に 0。 */
export async function fetchAdminAttention(): Promise<AdminAttention> {
  if (API_BASE) {
    const res = await apiFetch('/admin/attention');
    if (!res.ok) throw new Error(`要対応件数の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminAttention;
  }
  return { openContacts: 0, openReports: 0, pendingDevelopers: 0, openSimilarityFlags: 0 };
}

/** タグマスタ一覧（要管理者）。各タグに usageCount（利用数）が付き、未登録タグは末尾に unregistered: true で入る。 */
export async function fetchAdminTags(): Promise<AdminTag[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/tags');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`タグ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminTag[];
  }
  // モックではアニメーション側の tags を追跡しないため、利用数は常に 0・未登録タグも生じない。
  return [...loadTags()]
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())
    .map((t) => ({ ...t, usageCount: 0 }));
}

/** タグを新規追加する（要管理者）。同名は 409 相当でエラー。 */
export async function createAdminTag(input: { name: string; description?: string }): Promise<AdminTag> {
  if (API_BASE) {
    const res = await apiFetch('/admin/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 400) throw new Error((await res.json()).error ?? 'name の指定が不正です');
    if (res.status === 409) throw new Error('同名のタグが既に存在します');
    if (!res.ok) throw new Error(`タグ追加に失敗しました (${res.status})`);
    return (await res.json()) as AdminTag;
  }
  const name = input.name.trim();
  if (!name) throw new Error('name は必須です');
  if (name.length > 30) throw new Error('name は 30 文字以内で指定してください');
  if (/\s/.test(name)) throw new Error('name に空白は含められません');
  const tags = loadTags();
  if (tags.some((t) => t.name === name)) throw new Error('同名のタグが既に存在します');
  const tag: AdminTag = {
    name,
    description: input.description?.trim() ? input.description.trim().slice(0, 200) : undefined,
    createdAt: new Date().toISOString(),
  };
  saveTags([...tags, tag]);
  return tag;
}

/** タグを削除する（要管理者）。 */
export async function deleteAdminTag(name: string): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/tags/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (res.status === 404) throw new Error('タグが見つかりません');
    if (!res.ok) throw new Error(`タグ削除に失敗しました (${res.status})`);
    return;
  }
  saveTags(loadTags().filter((t) => t.name !== name));
}

/**
 * タグの説明編集・リネームを行う（要管理者）。description は空文字列で削除。
 * newName を指定するとタグ名を変更する（既存タグと同名なら統合）。レスポンスの renamedCount は
 * リネーム時のみ入り、書き換えたアニメーション件数を表す（説明編集のみの場合は無い）。
 */
export async function updateAdminTag(
  name: string,
  patch: { description?: string; newName?: string },
): Promise<AdminTag & { renamedCount?: number }> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/tags/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (res.status === 404) throw new Error('タグが見つかりません');
    if (res.status === 400) throw new Error(((await res.json()) as { error?: string }).error ?? '入力内容が不正です');
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminTag & { renamedCount?: number };
  }
  const tags = loadTags();
  const idx = tags.findIndex((t) => t.name === name);
  if (idx === -1) throw new Error('タグが見つかりません');

  const newName = patch.newName?.trim();
  const renaming = !!newName && newName !== name;
  if (renaming) {
    if (newName!.length > 30) throw new Error('newName は 30 文字以内で指定してください');
    if (/\s/.test(newName!)) throw new Error('newName に空白は含められません');
  }

  const description =
    patch.description !== undefined
      ? patch.description.trim()
        ? patch.description.trim().slice(0, 200)
        : undefined
      : tags[idx].description;

  if (renaming) {
    const mergeIdx = tags.findIndex((t) => t.name === newName);
    if (mergeIdx !== -1) {
      // 統合: 新名側のタグを残し（説明の指定があれば上書き）、旧タグは削除する。
      const merged: AdminTag = {
        ...tags[mergeIdx],
        description: patch.description !== undefined ? description : tags[mergeIdx].description,
      };
      saveTags([...tags.filter((t) => t.name !== name && t.name !== newName), merged]);
      // モックではアニメーション側の tags を追跡しないため renamedCount は常に 0。
      return { ...merged, renamedCount: 0 };
    }
    const renamed: AdminTag = { ...tags[idx], name: newName!, description };
    const next = [...tags];
    next[idx] = renamed;
    saveTags(next);
    return { ...renamed, renamedCount: 0 };
  }

  const updated: AdminTag = { ...tags[idx], description };
  const next = [...tags];
  next[idx] = updated;
  saveTags(next);
  return updated;
}

/** カテゴリマスタ一覧（要管理者）。 */
export async function fetchAdminCategories(): Promise<AdminCategory[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/categories');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`カテゴリ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminCategory[];
  }
  return [...loadCategories()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/** カテゴリを新規追加する（要管理者）。同名は 409 相当でエラー。 */
export async function createAdminCategory(input: { name: string; description?: string }): Promise<AdminCategory> {
  if (API_BASE) {
    const res = await apiFetch('/admin/categories', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 400) throw new Error((await res.json()).error ?? 'name の指定が不正です');
    if (res.status === 409) throw new Error('同名のカテゴリが既に存在します');
    if (!res.ok) throw new Error(`カテゴリ追加に失敗しました (${res.status})`);
    return (await res.json()) as AdminCategory;
  }
  const name = input.name.trim();
  if (!name) throw new Error('name は必須です');
  if (name.length > 30) throw new Error('name は 30 文字以内で指定してください');
  if (/\s/.test(name)) throw new Error('name に空白は含められません');
  const categories = loadCategories();
  if (categories.some((c) => c.name === name)) throw new Error('同名のカテゴリが既に存在します');
  const category: AdminCategory = {
    name,
    description: input.description?.trim() ? input.description.trim().slice(0, 200) : undefined,
    createdAt: new Date().toISOString(),
  };
  saveCategories([...categories, category]);
  return category;
}

/** カテゴリを削除する（要管理者）。既存アニメーションの category 文字列には影響しない。 */
export async function deleteAdminCategory(name: string): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/categories/${encodeURIComponent(name)}`, { method: 'DELETE' });
    if (res.status === 404) throw new Error('カテゴリが見つかりません');
    if (!res.ok) throw new Error(`カテゴリ削除に失敗しました (${res.status})`);
    return;
  }
  saveCategories(loadCategories().filter((c) => c.name !== name));
}

/** カテゴリの説明を編集する（要管理者）。空文字列を渡すと説明を削除する。 */
export async function updateAdminCategory(
  name: string,
  patch: { description?: string },
): Promise<AdminCategory> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/categories/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (res.status === 404) throw new Error('カテゴリが見つかりません');
    if (res.status === 400) throw new Error(((await res.json()) as { error?: string }).error ?? '説明の指定が不正です');
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminCategory;
  }
  const categories = loadCategories();
  const idx = categories.findIndex((c) => c.name === name);
  if (idx === -1) throw new Error('カテゴリが見つかりません');
  const description = patch.description?.trim() ? patch.description.trim().slice(0, 200) : undefined;
  const updated: AdminCategory = { ...categories[idx], description };
  const next = [...categories];
  next[idx] = updated;
  saveCategories(next);
  return updated;
}

/** VPM (Unityパッケージ) の全バージョン一覧（新着順、配信中/停止中とも、要管理者）。 */
export async function fetchAdminVpmVersions(): Promise<AdminVpmVersion[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/vpm/versions');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`VPMバージョン一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AdminVpmVersion[];
  }
  // モックモードではバージョン管理を再現しない（常に空一覧）。
  return [];
}

/** VPM バージョンの配信ステータスを変更する（要管理者）。 */
export async function updateAdminVpmVersion(
  packageName: string,
  version: string,
  status: VpmVersionStatus,
): Promise<AdminVpmVersion> {
  if (API_BASE) {
    const res = await apiFetch(
      `/admin/vpm/versions/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    );
    if (res.status === 404) throw new Error('バージョンが見つかりません');
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminVpmVersion;
  }
  throw new Error('モックモードではVPMバージョン管理はサポートされていません。');
}

/** VPM バージョンを完全に削除する（zip実体もS3から削除、要管理者）。 */
export async function deleteAdminVpmVersion(packageName: string, version: string): Promise<void> {
  if (API_BASE) {
    const res = await apiFetch(
      `/admin/vpm/versions/${encodeURIComponent(packageName)}/${encodeURIComponent(version)}`,
      { method: 'DELETE' },
    );
    if (res.status === 404) throw new Error('バージョンが見つかりません');
    if (!res.ok) throw new Error(`削除に失敗しました (${res.status})`);
    return;
  }
  throw new Error('モックモードではVPMバージョン管理はサポートされていません。');
}

/** 問い合わせ履歴一覧（新着順、要管理者）。cursor を渡すと続きのページを取得する。 */
export async function fetchAdminContacts(
  cursor?: string,
): Promise<{ items: AdminContact[]; cursor?: string }> {
  if (API_BASE) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const res = await apiFetch(`/admin/contacts${qs}`);
    if (!res.ok) throw new Error(`問い合わせ一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as { items: AdminContact[]; cursor?: string };
  }
  // モックでは問い合わせ履歴の保存を再現しないため常に空。
  return { items: [], cursor: undefined };
}

/** 問い合わせの対応ステータスを変更する（要管理者）。 */
export async function updateAdminContact(
  id: string,
  patch: { status: ContactStatus },
): Promise<AdminContact> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/contacts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminContact;
  }
  throw new Error('モックモードでは問い合わせ管理はサポートされていません。');
}

/** 通報一覧（新着順、要管理者）。cursor を渡すと続きのページを取得する。 */
export async function fetchAdminReports(
  cursor?: string,
): Promise<{ items: AdminReport[]; cursor?: string }> {
  if (API_BASE) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const res = await apiFetch(`/admin/reports${qs}`);
    if (!res.ok) throw new Error(`通報一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as { items: AdminReport[]; cursor?: string };
  }
  // モックでは通報の保存を再現しないため常に空。
  return { items: [], cursor: undefined };
}

/** 通報の対応ステータスを変更する（要管理者）。 */
export async function updateAdminReport(
  id: string,
  patch: { status: ReportStatus },
): Promise<AdminReport> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/reports/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminReport;
  }
  throw new Error('モックモードでは通報管理はサポートされていません。');
}

/** モーション類似検出結果一覧（新着順、要管理者）。cursor を渡すと続きのページを取得する。 */
export async function fetchAdminSimilarityFlags(
  cursor?: string,
): Promise<{ items: AdminSimilarityFlag[]; cursor?: string }> {
  if (API_BASE) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const res = await apiFetch(`/admin/similarity-flags${qs}`);
    if (!res.ok) throw new Error(`類似検出結果の取得に失敗しました (${res.status})`);
    return (await res.json()) as { items: AdminSimilarityFlag[]; cursor?: string };
  }
  // モックでは類似検出結果の保存を再現しないため常に空。
  return { items: [], cursor: undefined };
}

/** 類似検出結果の対応ステータスを変更する（要管理者）。 */
export async function updateAdminSimilarityFlag(
  id: string,
  patch: { status: 'open' | 'closed' },
): Promise<AdminSimilarityFlag> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/similarity-flags/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AdminSimilarityFlag;
  }
  throw new Error('モックモードでは類似検出結果の管理はサポートされていません。');
}

/** 管理操作の監査ログ一覧（新着順、要管理者）。cursor を渡すと続きのページを取得する。 */
export async function fetchAdminAuditLogs(
  cursor?: string,
): Promise<{ items: AdminAuditLog[]; cursor?: string }> {
  if (API_BASE) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const res = await apiFetch(`/admin/audit-logs${qs}`);
    if (!res.ok) throw new Error(`監査ログの取得に失敗しました (${res.status})`);
    return (await res.json()) as { items: AdminAuditLog[]; cursor?: string };
  }
  // モックでは監査ログの保存を再現しないため常に空。
  return { items: [], cursor: undefined };
}

/** pose-server（PC常駐GPU推論サーバー）を起動する（要管理者）。失敗時は 503 の error メッセージを投げる。 */
export async function startAdminPoseServer(): Promise<{ state: string }> {
  if (API_BASE) {
    const res = await apiFetch('/admin/pose-server/start', { method: 'POST' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? `ポーズサーバーの起動に失敗しました (${res.status})`);
    }
    return (await res.json()) as { state: string };
  }
  throw new Error('モック環境では利用できません');
}

/** pose-server を停止する（要管理者）。失敗時は 503 の error メッセージを投げる。 */
export async function stopAdminPoseServer(): Promise<{ state: string }> {
  if (API_BASE) {
    const res = await apiFetch('/admin/pose-server/stop', { method: 'POST' });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? `ポーズサーバーの停止に失敗しました (${res.status})`);
    }
    return (await res.json()) as { state: string };
  }
  throw new Error('モック環境では利用できません');
}

/**
 * 利用者からの画像受付ON/OFFを切り替える（要管理者）。
 * ON にしておくと利用者が画像を送ったときだけ pose-server が自動起動し、生成後5分アイドルで
 * 自動停止する（省電力運用）。OFF にすると画像が届いても起動せず、稼働中のサーバーは即停止する。
 * 失敗時は 503 の error メッセージを投げる。
 */
export async function setAdminPoseAccepting(accepting: boolean): Promise<{ accepting: boolean; state: string }> {
  if (API_BASE) {
    const res = await apiFetch('/admin/pose-server/accepting', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accepting }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(body?.error ?? `受付設定の変更に失敗しました (${res.status})`);
    }
    return (await res.json()) as { accepting: boolean; state: string };
  }
  throw new Error('モック環境では利用できません');
}

/** 連携アプリケーションの掲載申請一覧（新しい順、要管理者）。 */
export async function fetchAdminAppListings(): Promise<AppListingInfo[]> {
  if (API_BASE) {
    const res = await apiFetch('/admin/app-listings');
    if (res.status === 403) throw new Error('管理者のみ閲覧できます');
    if (!res.ok) throw new Error(`掲載申請一覧の取得に失敗しました (${res.status})`);
    return (await res.json()) as AppListingInfo[];
  }
  // モックでは自分の申請一覧のみが対象（複数開発者の管理は再現できない）。
  return [...loadAppListings()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

/** 掲載申請のステータスを変更する（承認/却下/停止、要管理者）。 */
export async function updateAppListing(
  appId: string,
  patch: { status: AppListingStatus },
): Promise<AppListingInfo> {
  if (API_BASE) {
    const res = await apiFetch(`/admin/app-listings/${encodeURIComponent(appId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) throw new Error(`更新に失敗しました (${res.status})`);
    return (await res.json()) as AppListingInfo;
  }
  const listings = loadAppListings();
  const idx = listings.findIndex((l) => l.appId === appId);
  if (idx === -1) throw new Error('対象の掲載申請が見つかりません');
  const updated: AppListingInfo = { ...listings[idx], status: patch.status, updatedAt: new Date().toISOString() };
  const next = [...listings];
  next[idx] = updated;
  saveAppListings(next);
  return updated;
}
